import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { UtilService } from 'src/app/modules/cop/services/util.service';
import { DistanceService } from 'src/app/modules/core/services/distance/distance.service';
import {
  Document,
  FieldDataType,
  Filter,
  FilterGroup,
  FilterGroupType,
  RuleOperator,
  SystemFieldNames
} from 'src/app/modules/core/store/models';
import { BasicDocumentReader } from 'src/app/modules/core/store/models/basic-document-reader';
import { Dataset, DatasetSubtype } from 'src/app/modules/core/store/models/dataset.model';
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash-es';
import { DataApiService } from '../../services/data/data-api.service';
import { ImpactedAssetsApiService } from '../../services/impacted-assets-api.service';
import * as copActions from '../cop/cop.actions';
import * as copSelectors from '../cop/cop.selectors';
import * as listsActions from '../lists/lists.actions';
import * as listActions from '../lists/lists.actions';
import moment from 'moment';
import { LayerDocuments, LayerTypes } from '../cop/models';
import { ListQuery } from '../lists/models/card.model';
import * as impactedActions from './impacted-assets.actions';
import {
  ClosestAssetCountByLayer,
  ClosestAssetFilterDto,
  DocumentSummaryByLayer,
  DocumentSummaryWithDistance,
  DocumentSummaryWithEmail,
  SelectionLayerFilter
} from './impacted-assets.model';
import * as impactedSelectors from './impacted-assets.selectors';
import { MapDrawingService } from '../../drawing/map-drawing.service';
import { FolderConfiguration } from '../lsd/models/map-configuration.model';
import { MapLayerQueryBuildService } from '../../services/data/map-layer-query-build.service';
import { DocumentDisplayNameService } from '@app/modules/core/services/document-display-name.service';

const ClosestAssetColumnName = 'topoone_metadata_closest_distance';
const LayerUIdColumnName = 'topoone_metadata_layerUId';

@Injectable()
export class ImpactedAssetsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private impactedAssetsApiService: ImpactedAssetsApiService,
    private utilService: UtilService,
    private translate: TranslateService,
    private distanceService: DistanceService,
    private mapDrawingService: MapDrawingService,
    private documentDisplayNameService: DocumentDisplayNameService
  ) {}

  calculateSelectedDocumentAssetsSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.calculateSelectedDocumentAssetsSummary),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectSelectedFloor),
        this.store$.select(copSelectors.selectLayerQuickFilterSelections),
        this.store$.select(copSelectors.selectSectionWidgetUserSettings)
      ),
      switchMap(([action, mapState, selectedFloor, quickFilters, sectionWidgetUserSettings]) => {
        const layersOfType = action.forEvent === 'selection' ? action.layersOfType : [LayerTypes.Asset];

        const geoDoc: Document = {};
        let filterGeo: string = null;
        let distanceGeo: string = null;
        let fromDate: string = null;
        let toDate: string = null;

        const selectedDocument = action.document;
        // calculate Assets Summary
        let isControlShown = false;
        let datasetUId = null;

        // Set filter shape from document field
        if (action.forEvent !== 'selection') {
          if (!selectedDocument || !mapState || !action.layer || action.layer?.type !== LayerTypes.Alert) {
            return [
              impactedActions.setClosestAssetCounts({
                countsByLayer: [],
                isControlShown
              }),
              impactedActions.setClosestCountIsWorking({ isWorking: false })
            ];
          }
          const selectedDocumentReader = new BasicDocumentReader(selectedDocument);

          // Passed document is an alert check
          isControlShown = true;

          datasetUId = action?.layer?.datasetUId;

          let geometryFields = mapState.datasetsSet[datasetUId]?.schema?.fields.filter(f => f.dataType === FieldDataType.Geometry);

          if (geometryFields?.length > 1) {
            // Use the selected geometry field, or the location field if present, otherwise the first Geometry field
            const locationField = geometryFields.find(f => f.systemName === SystemFieldNames.location);
            const geoFieldsToDisplay = action?.layer.displayOptions?.geoFieldsToDisplay;
            const firstSelectedGeometryField = geometryFields.find(
              f => geoFieldsToDisplay && geoFieldsToDisplay.length > 0 && geoFieldsToDisplay[0] === f.systemName
            );
            geometryFields = [firstSelectedGeometryField ?? locationField ?? geometryFields[0]];
          }

          if (geometryFields?.length === 1 && geometryFields[0]) {
            let geometry = selectedDocumentReader.getFieldValue(geometryFields[0].systemName);

            if (geometry) {
              filterGeo = geometry;
            }
          }

          const dates = this.getSearchDatesFromDocument(selectedDocument);
          if (dates) {
            fromDate = dates.fromDate;
            toDate = dates.toDate;
          }
        }

        // Set filter shape from action
        if (action.shape?.length > 0) {
          // If we are using a shape to filter, preserve the original alert geo for distance calculation
          distanceGeo = filterGeo;
          filterGeo = JSON.stringify(this.utilService.packFeaturesArrayAfterEditingIntoGeoJson(action.shape));
        }

        if (!filterGeo) {
          return [
            impactedActions.setClosestAssetCounts({
              countsByLayer: [],
              isControlShown
            }),
            impactedActions.setClosestCountIsWorking({ isWorking: false })
          ];
        }

        const activeSelectedLayers =
          mapState?.layers?.filter(
            sl =>
              (!layersOfType || layersOfType.includes(sl.type)) &&
              sl.isActive &&
              sl.datasetUId &&
              (!action?.visibleOnly || !!this.mapDrawingService.visibilityService.isLayerVisible(sl.uId))
          ) || [];

        var allClosest = [];
        var emailAssets = [];
        var emailsExist = false;

        activeSelectedLayers.forEach(layer => {
          var stepsVisible = [];
          if (layer.type === LayerTypes.Alert && !!action?.visibleOnly) {
            const settings = this.mapDrawingService.visibilityService.getLayerVisibilitySettings(layer);

            if (!!settings?.section?.uId && !!settings?.section?.configuration) {
              const folderConfig: FolderConfiguration = JSON.parse(settings.section.configuration);
              var userFolderSetting = sectionWidgetUserSettings[settings.section.uId];
              var showByWorkflow = !userFolderSetting?.viewMode
                ? folderConfig?.viewMode === 'workflow'
                : userFolderSetting.viewMode === 'workflow-step';

              if (showByWorkflow) {
                stepsVisible = this.mapDrawingService.visibilityService.getVisibleDatasetWorkflowSteps(layer, settings);
                if (stepsVisible?.length === 0) {
                  return;
                }
              }
            }
          }

          let query = MapLayerQueryBuildService.buildLayerQueryParams(
            layer,
            mapState.datasetsSet[layer.datasetUId],
            quickFilters[layer.uId],
            null,
            null
          );
          let filterGroup = query?.filterGroup;

          // Generate Workflow Step Filters For Alerts
          if (stepsVisible?.length > 0) {
            let wfFilters: Filter[] = [];

            stepsVisible.forEach(step => {
              wfFilters.push({
                field: 'workflow_step_uid',
                operator: RuleOperator.Equal,
                value: step
              });
            });

            const stepFilterGroup = {
              filters: wfFilters,
              filterGroups: [],
              groupType: FilterGroupType.Or
            };

            if (!!filterGroup) {
              const encapsulatingFilterGroup = {
                filters: [],
                filterGroups: [],
                groupType: FilterGroupType.And
              };

              encapsulatingFilterGroup.filterGroups.push(filterGroup);
              encapsulatingFilterGroup.filterGroups.push(stepFilterGroup);
              filterGroup = encapsulatingFilterGroup;
            } else {
              filterGroup = stepFilterGroup;
            }
          }

          var layerState: SelectionLayerFilter = {
            queryMatchRuleDto: {
              filterGroup: filterGroup,
              fields: null,
              pagination: {
                take: layer.displayOptions?.maxItemsToRender || 1000,
                skip: 0
              }
            },
            layerUId: layer.uId,
            layerName: layer.name,
            datasetUId: layer.datasetUId,
            floorFilterSourceField: layer.displayOptions.floorFilterSourceField
          };

          allClosest.push(layerState);

          const emailFields = mapState.datasetsSet[layer.datasetUId]?.schema?.fields.filter(f => f.dataType === FieldDataType.Email);
          if (emailFields?.length > 0) {
            emailsExist = true;

            let emailFilterGrp: FilterGroup = {
              uId: uuidv4(),
              filters: [],
              filterGroups: [],
              groupType: FilterGroupType.Or
            };

            emailFields.forEach(e => {
              let foundField = {
                uId: uuidv4(),
                field: e.systemName,
                operator: RuleOperator.IsNotEmpty,
                value: null
              };

              emailFilterGrp.filters.push(foundField);
            });

            if (!!filterGroup) {
              const encapsulatingEmailFilterGroup = {
                filters: [],
                filterGroups: [],
                groupType: FilterGroupType.And
              };

              encapsulatingEmailFilterGroup.filterGroups.push(filterGroup);
              encapsulatingEmailFilterGroup.filterGroups.push(emailFilterGrp);
              filterGroup = encapsulatingEmailFilterGroup;
            } else {
              filterGroup = emailFilterGrp;
            }

            var emailState: SelectionLayerFilter = {
              queryMatchRuleDto: {
                filterGroup: cloneDeep(filterGroup),
                fields: null,
                pagination: null
              },
              layerUId: layer.uId,
              datasetUId: layer.datasetUId
            };

            emailAssets.push(emailState);
          }
        });

        geoDoc['filterGeo'] = filterGeo;

        var closestAssetDto: ClosestAssetFilterDto = {
          locationGeoJson: geoDoc,
          distanceGeoJson: distanceGeo,
          layerStates: allClosest,
          pagination: null,
          withinMeters: action.withinMeters,
          fromDate,
          toDate
        };

        var closestAssetSelection: ClosestAssetFilterDto = {
          locationGeoJson: geoDoc,
          distanceGeoJson: distanceGeo,
          layerStates: allClosest,
          returnedDocumentLimit: 0,
          pagination: null,
          withinMeters: action.withinMeters,
          selectedFloor: selectedFloor,
          fromDate,
          toDate
        };

        var emailAssetDto: ClosestAssetFilterDto = {
          locationGeoJson: geoDoc,
          distanceGeoJson: distanceGeo,
          layerStates: emailAssets,
          returnedDocumentLimit: 0,
          pagination: null,
          withinMeters: action.withinMeters,
          fromDate,
          toDate
        };

        return action.forEvent === 'selection'
          ? [
              impactedActions.setAssetEmailsExist({ doEmailsExist: emailsExist }),
              impactedActions.setClosestAssetCounts({
                countsByLayer: [],
                isControlShown
              }),
              impactedActions.setSelectionEmailMeta({ selectionFilterDto: emailAssetDto }),
              impactedActions.setClosestAssetRequest({ closestAssetDto: closestAssetDto, forEvent: action.forEvent }),
              impactedActions.setClosestIsWorking({ isWorking: true }),
              impactedActions.requestClosestAssets({ closestAssetDto: closestAssetSelection, forEvent: action.forEvent })
            ]
          : [
              impactedActions.setAssetEmailsExist({ doEmailsExist: emailsExist }),
              impactedActions.setClosestAssetCounts({
                countsByLayer: [],
                isControlShown
              }),
              impactedActions.setSelectionEmailMeta({ selectionFilterDto: emailAssetDto }),
              impactedActions.setCommunicationMeta({ isDrawn: action.shape?.length > 0, metersWithin: action.withinMeters }),
              impactedActions.setClosestAssetRequest({ closestAssetDto: closestAssetDto, forEvent: action.forEvent }),
              impactedActions.setClosestCountIsWorking({ isWorking: true }),
              impactedActions.requestClosestAssetCounts({ closesetAssetDto: closestAssetDto })
            ];
      })
    )
  );

  requestClosestAssetsByLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.requestClosestAssetsByLayer),
      withLatestFrom(this.store$.select(impactedSelectors.selectClosestAssetRequest)),
      switchMap(([action, meta]) => {
        return this.impactedAssetsApiService.calculateClosestAssetsForLayer(meta.assetRequest, action.layerUId).pipe(
          switchMap(results => {
            return [impactedActions.setClosestAsset({ docs: results.data, forEvent: meta.forEvent })];
          }),
          catchError(err => of(impactedActions.setClosestAsset({ docs: [], forEvent: meta.forEvent }), copActions.setError({ err })))
        );
      })
    )
  );

  requestUnloadedClosestAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.requestUnloadedClosestAssets),
      withLatestFrom(this.store$.select(impactedSelectors.selectClosestAssetRequest)),
      switchMap(([action, meta]) => {
        const layerstatesToFetch = meta?.assetRequest?.layerStates?.filter(ls => action.layerUIds.includes(ls.layerUId));

        var request: ClosestAssetFilterDto = {
          ...meta.assetRequest,
          returnedDocumentLimit: 0,
          layerStates: layerstatesToFetch,
          pagination: null
        };

        return this.impactedAssetsApiService.calculateClosestAssets(request).pipe(
          switchMap(results => {
            return [impactedActions.setClosestAsset({ docs: results, forEvent: meta.forEvent })];
          }),
          catchError(err => of(impactedActions.setClosestAsset({ docs: [], forEvent: meta.forEvent }), copActions.setError({ err })))
        );
      })
    )
  );

  requestClosestAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.requestClosestAssets),
      withLatestFrom(this.store$.select(impactedSelectors.selectClosestAssetRequest)),

      switchMap(([action, meta]) => {
        return this.impactedAssetsApiService.calculateClosestAssets(action.closestAssetDto).pipe(
          switchMap(results => {
            const listQuery: ListQuery = {
              updateDate: new Date().getTime().toString(),
              dataUpdateTime: {},
              data: {
                type: 'closest-asset',
                headerLabel: this.translate.instant('MAP.CLOSEST-ASSETS'),
                layerQueries: {},
                assetParams: {
                  locationGeoJson: meta.assetRequest.locationGeoJson,
                  distanceGeoJson: null,
                  withinMeters: meta.assetRequest.withinMeters,
                  fromDate: meta.assetRequest.fromDate,
                  toDate: meta.assetRequest.toDate
                }
              }
            };

            const layerStatesWithCounts = meta.assetRequest.layerStates.filter(
              ls => !!results.find(ca => ca.topoone_metadata_layerUId === ls.layerUId)
            );

            layerStatesWithCounts.forEach(ls => {
              listQuery.data.layerQueries[ls.layerUId] = {
                query: {
                  filterGroup: ls.queryMatchRuleDto.filterGroup,
                  pagination: ls.queryMatchRuleDto.pagination,
                  fields: null,
                  calcFields: null,
                  sort: null
                }
              };
            });

            return [
              listsActions.setSelectListQueryToStore({ query: listQuery }),
              impactedActions.setClosestAsset({ docs: results, forEvent: action.forEvent })
            ];
          }),
          catchError(err => of(impactedActions.setClosestAsset({ docs: [], forEvent: action.forEvent }), copActions.setError({ err })))
        );
      })
    )
  );

  setClosestAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.setClosestAsset),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState), this.store$.select(copSelectors.selectDatasetsSet)),
      switchMap(([action, selectedMap, datasetsSet]) => {
        let docSummaries: DocumentSummaryWithDistance[] = [];
        const foundDocs: { layerUId: string; documents: Document[] }[] = [];
        var layerUIds = [];

        action.docs.forEach(doc => {
          let docReader = new BasicDocumentReader(doc);
          const layerUId = docReader.getFieldValue(LayerUIdColumnName);
          let layer = selectedMap.layersSet[layerUId];

          if (!layerUIds.some(l => l === layerUId)) {
            layerUIds.push(layerUId);
          }

          if (action.forEvent === 'selection') {
            if (!foundDocs.find(f => f.layerUId === layerUId)) {
              foundDocs.push({ layerUId, documents: [] });
            }
            foundDocs.find(f => f.layerUId === layerUId).documents.push(doc);
            return;
          }

          const dataset = datasetsSet[layer.datasetUId];

          let documentName = this.documentDisplayNameService.getDisplayName(dataset, docReader);

          let tempSummary: DocumentSummaryWithDistance = {
            distance: this.distanceService.parseToDistanceToWholeUnits(docReader.getFieldValue(ClosestAssetColumnName)),
            distanceText: this.distanceService.parseToFriendlyDistance(docReader.getFieldValue(ClosestAssetColumnName)),
            unitSystem: this.distanceService.unitSystem(),
            document: doc,
            layer: layer,
            name: documentName,
            pinStyle: null
          };

          docSummaries.push(tempSummary);
        });

        if (action.forEvent === 'selection') {
          var layerDocs: LayerDocuments = {};
          var totalCount = 0;

          foundDocs.forEach(foundDoc => {
            totalCount += foundDoc.documents.length;
            layerDocs[foundDoc.layerUId] = foundDoc.documents;
          });

          return [
            listActions.loadLayerDocumentsIntoCards({
              layerDocuments: layerDocs,
              listType: action.forEvent === 'selection' ? 'selection' : 'assets',
              totalCount: totalCount
            }),
            impactedActions.setClosestIsWorking({ isWorking: false })
          ];
        }

        var summaries: DocumentSummaryByLayer[] = [];

        layerUIds.forEach(l => {
          const summariesByLayer: DocumentSummaryByLayer = {
            layerUId: l,
            summariesWithDistance: docSummaries.filter(ds => ds.layer.uId === l).slice(0, 200)
          };

          summaries.push(summariesByLayer);
        });

        // If distances are exactly the same, sort on name
        summaries.forEach(sum =>
          sum.summariesWithDistance.sort((a, b) => {
            if (a.distance === b.distance) {
              const an = a.name?.toLocaleLowerCase(),
                bn = b.name?.toLocaleLowerCase();
              if (bn === an) {
                return 0;
              }
              return an > bn ? 1 : -1;
            }
            return a.distance - b.distance;
          })
        );

        return [
          impactedActions.addSelectedDocumentAssetsSummary({
            assetsByDistance: summaries
          }),
          impactedActions.setClosestIsWorking({ isWorking: false })
        ];
      })
    )
  );

  refreshImpactedAssetsByLayerDisplay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.refreshImpactedAssetsByLayerDisplay),
      withLatestFrom(this.store$.select(impactedSelectors.selectSelectedDocumentAssetsByDistance)),
      switchMap(([, impactedAssetsLayerSummaries]) => {
        if (!impactedAssetsLayerSummaries.length) {
          return [];
        }

        const selectedDocumentAssetsByDistance = impactedAssetsLayerSummaries.map(layerSummary => {
          const summariesWithDistance = layerSummary.summariesWithDistance.map(summary => {
            const docReader = new BasicDocumentReader(summary.document);
            return {
              ...summary,
              distance: this.distanceService.parseToDistanceToWholeUnits(docReader.getFieldValue(ClosestAssetColumnName)),
              distanceText: this.distanceService.parseToFriendlyDistance(docReader.getFieldValue(ClosestAssetColumnName)),
              unitSystem: this.distanceService.unitSystem()
            };
          });

          return {
            layerUId: layerSummary.layerUId,
            summariesWithDistance
          };
        });

        return [impactedActions.setSelectedDocumentAssetsByDistance({ selectedDocumentAssetsByDistance })];
      })
    )
  );

  getAssetEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.getAssetEmails),
      withLatestFrom(this.store$.select(impactedSelectors.selectEmailMeta)),
      switchMap(([, emailMeta]) => {
        return this.impactedAssetsApiService.calculateClosestAssets(emailMeta.selectionFilterDto).pipe(
          map(results => impactedActions.setAssetEmailSummary({ docs: results })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  setAssetEmailSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.setAssetEmailSummary),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectDatasetsSet),
        this.store$.select(impactedSelectors.selectEmailMeta)
      ),
      switchMap(([action, selectedMap, datasetsSet, emailMeta]) => {
        let emailDocuments: DocumentSummaryWithEmail[] = [];

        action.docs.forEach(doc => {
          let docReader = new BasicDocumentReader(doc);
          const layer = selectedMap.layersSet[docReader.getFieldValue(LayerUIdColumnName)];
          const dataset = datasetsSet[layer.datasetUId];
          const emailFields = dataset?.schema?.fields?.filter(f => f.dataType === FieldDataType.Email);

          if (!!emailFields) {
            emailFields.forEach(element => {
              let documentName = this.documentDisplayNameService.getDisplayName(dataset, docReader);

              var field = docReader.getFieldValue(element.systemName);
              if (field) {
                var emails = field.split(',')?.length > 1 ? field.split(',') : field.split(';');

                for (var val of emails) {
                  let tempEmailSummary: DocumentSummaryWithEmail = {
                    distanceText: this.distanceService.parseToFriendlyDistance(docReader.getFieldValue(ClosestAssetColumnName)),
                    layer: layer,
                    name: documentName,
                    pinStyle: null,
                    emailAddr: val,
                    distance: this.distanceService.parseToDistanceToWholeUnits(docReader.getFieldValue(ClosestAssetColumnName))
                  };

                  emailDocuments.push(tempEmailSummary);
                }
              }
            });
          }
        });

        return [
          impactedActions.setAssetEmailSummaryComplete({ emailSummaries: emailDocuments }),
          impactedActions.setAssetEmailIsWorking({ isWorking: false })
        ];
      })
    )
  );

  requestClosestAssetCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.requestClosestAssetCounts),
      switchMap(action =>
        this.impactedAssetsApiService.getClosestAssetCountsByLayer(action.closesetAssetDto).pipe(
          map(results => {
            return impactedActions.requestClosestAssetCountsComplete({ results, forEvent: action.forEvent });
          }),
          catchError(err => of(impactedActions.setClosestAsset({ docs: [], forEvent: action.forEvent }), copActions.setError({ err })))
        )
      )
    )
  );

  requestClosestAssetCountsComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.requestClosestAssetCountsComplete),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(impactedSelectors.selectClosestAssetRequest)
      ),
      switchMap(([action, mapState, closestMeta]) => {
        if (!closestMeta.assetRequest) {
          return [impactedActions.setClosestCountIsWorking({ isWorking: false })];
        }

        let closestAssetCountsByLayer: ClosestAssetCountByLayer[] = [];

        action.results.forEach(doc => {
          const layer = mapState.layersSet[doc.layerUId];

          closestAssetCountsByLayer.push({
            count: doc.count,
            layer: layer
          });
        });

        const listQuery: ListQuery = {
          updateDate: new Date().getTime().toString(),
          dataUpdateTime: {},
          data: {
            type: 'closest-asset',
            headerLabel: this.translate.instant('EDIT-PANEL.SELECTED-ITEMS'),
            layerQueries: {},
            assetParams: {
              locationGeoJson: closestMeta.assetRequest.locationGeoJson,
              distanceGeoJson: closestMeta.assetRequest.distanceGeoJson,
              withinMeters: closestMeta.assetRequest.withinMeters,
              fromDate: closestMeta.assetRequest.fromDate,
              toDate: closestMeta.assetRequest.toDate
            }
          }
        };

        const layerStatesWithCounts = closestMeta.assetRequest.layerStates.filter(
          ls => !!closestAssetCountsByLayer.find(ca => ca.layer.uId === ls.layerUId)
        );

        layerStatesWithCounts.forEach(ls => {
          listQuery.data.layerQueries[ls.layerUId] = {
            query: {
              filterGroup: ls.queryMatchRuleDto.filterGroup,
              pagination: ls.queryMatchRuleDto.pagination,
              fields: null,
              calcFields: null,
              sort: null
            }
          };
        });

        return [
          listsActions.setAssetsListQueryToStore({ query: listQuery }),
          impactedActions.setClosestAssetCounts({
            countsByLayer: closestAssetCountsByLayer,
            isControlShown: true
          }),
          impactedActions.setClosestCountIsWorking({ isWorking: false })
        ];
      })
    )
  );

  loadRemainingClosestAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(impactedActions.loadRemainingClosestAssets),
      withLatestFrom(
        this.store$.select(impactedSelectors.selectSelectedDocumentAssetsByDistance),
        this.store$.select(impactedSelectors.selectSelectedDocumentClosestAssetCounts)
      ),
      switchMap(([action, impactedAssets, impactedLayers]) => {
        const loadedLayerUIds = impactedAssets?.map(a => a.layerUId);
        const layersToLoad = impactedLayers?.countsByLayer?.map(c => c.layer.uId)?.filter(l => !loadedLayerUIds?.includes(l));

        if (!layersToLoad || layersToLoad?.length === 0) {
          return [impactedActions.setClosestIsWorking({ isWorking: false })];
        }

        return [
          impactedActions.requestUnloadedClosestAssets({
            layerUIds: layersToLoad
          })
        ];
      })
    )
  );

  getSearchDatesFromDocument(selectedDocument: Document): { fromDate?: string; toDate?: string } {
    if (!!!selectedDocument) {
      return null;
    }

    if (selectedDocument.start_date && selectedDocument.end_date) {
      const fromDate = moment(selectedDocument.start_date);
      const toDate = moment(selectedDocument.end_date);
      return {
        fromDate: fromDate.subtract(8, 'hours').toISOString(),
        toDate: toDate.add(8, 'hours').toISOString()
      };
    } else if (selectedDocument.posted_date) {
      const postedDate = moment(selectedDocument.posted_date);
      return {
        fromDate: postedDate.subtract(8, 'hours').toISOString(),
        toDate: postedDate.add(8, 'hours').toISOString()
      };
    } else if (selectedDocument.date_created) {
      const dateCreated = moment(selectedDocument.date_created);
      return {
        fromDate: dateCreated.subtract(8, 'hours').toISOString(),
        toDate: dateCreated.add(8, 'hours').toISOString()
      };
    } else {
      const nowDate = moment();
      return {
        fromDate: nowDate.subtract(8, 'hours').toISOString(),
        toDate: nowDate.add(8, 'hours').toISOString()
      };
    }
  }
}
